import React from 'react'
import { ConfigProvider, Menu } from 'antd'
import { ISettingsNavGroup } from './registry'
import { ICON_ASSIGNMENTS, MenuIcon } from '@pushly/aqe/lib/icons'
import type { SlotId } from '@pushly/aqe/lib/icons'

interface ISettingsRail<C> {
    groups: ISettingsNavGroup<C>[]
    currentKey: string
    onNavigate: (key: string) => void
    /** Pages still waiting on their flags: their rows are held as placeholders so the rail does not grow late. */
    pendingCount?: number
    /** Which settings rail this is, to find each row's Pushly icon. Without it the rail has no icons. */
    iconContext?: 'domain' | 'org'
}

/** Left padding of an item, and of a group title, so the two share an edge. */
const RAIL_INLINE_INDENT = 12

/** Sizes only; colours and rules are in settings-layout.scss, from the theme's CSS variables. */
const RAIL_MENU_TOKENS = {
    itemHeight: 30,
    groupTitleFontSize: 12,
}

/**
 * The page navigation: an antd inline Menu with a group per registry group. Keyboard handling,
 * selection and theming are antd's; the surrounding `nav` gives it a landmark and a name.
 */
/** The Pushly icon assigned to a rail row: filled for the current page, outlined otherwise. */
const railIcon = (context: 'domain' | 'org' | undefined, key: string, current: boolean) => {
    const name = context ? ICON_ASSIGNMENTS[`rail:${context}:${key}` as SlotId] : undefined
    return name ? <MenuIcon name={name} current={current} aria-hidden="true" /> : undefined
}

export const SettingsRail = <C,>({
    groups,
    currentKey,
    onNavigate,
    pendingCount = 0,
    iconContext,
}: ISettingsRail<C>) => (
    <div className="settings-rail-col">
        <nav className="settings-rail" aria-label="Settings">
            <ConfigProvider theme={{ components: { Menu: RAIL_MENU_TOKENS } }}>
                <Menu
                    mode="inline"
                    // Items start flush with the group titles' text; antd would indent them 24px.
                    inlineIndent={RAIL_INLINE_INDENT}
                    selectedKeys={[currentKey]}
                    onClick={({ key, domEvent }) => {
                        // A modified click is the browser's: the link opens in a new tab and this tab stays put.
                        if ('metaKey' in domEvent && (domEvent.metaKey || domEvent.ctrlKey || domEvent.shiftKey)) return
                        domEvent.preventDefault()
                        onNavigate(key)
                    }}
                    items={groups.map((group) => ({
                        type: 'group' as const,
                        key: `group-${group.key}`,
                        label: group.label,
                        children: group.pages.map((p) => ({
                            key: p.key,
                            icon: railIcon(iconContext, p.key, p.key === currentKey),
                            // A real link, kept out of the tab order: the Menu item is the one keyboard stop.
                            label: (
                                <a
                                    href={`#${p.key}`}
                                    tabIndex={-1}
                                    aria-current={p.key === currentKey ? 'page' : undefined}
                                >
                                    {p.label}
                                </a>
                            ),
                        })),
                    }))}
                />
            </ConfigProvider>
            {pendingCount > 0 && (
                <div className="settings-rail-pending" aria-hidden="true">
                    {Array.from({ length: pendingCount }, (_, i) => (
                        <span key={i} />
                    ))}
                </div>
            )}
        </nav>
    </div>
)
